import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {SampleStatusItem, SampleStatusWarningType} from 'constants/SampleStatusConstants';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  doesCurrentUserHaveAccessForItem,
  isItemAvailableToCurrentUser,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {
  SampleStatusWarningTrackingParams,
  trackStatusWarningChanges,
  trackStatusWarningDismissed,
} from 'inventory/sample-status/SampleStatusWarningTracking';
import {InlineBannerActionType, InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import useOnMount from 'ui-library/hooks/useOnMount';

export interface SampleStatusWarningBannerStackProps {
  trackingParamsWithSampleIds: SampleStatusWarningTrackingParams;
  canDeselectSamples: boolean;
  shouldProceed: boolean;
  onChangeShouldProceed: (shouldProceed: boolean) => void;
  hasOverridableWarnings?: boolean;
  onChangeHasOverridableWarnings?: (hasOverridable: boolean) => void;
  samples: Array<SampleStatusItem>;
  selectedSampleIds: Array<string>;
  setSelectedSampleIds: (newSelectedSampleIds: Array<string>) => void;
}

interface SingleStatusWarningBannerConfigProps {
  isBlocking: boolean;
  warningType: SampleStatusWarningType;
  canDismissBanner: boolean;
  onDismissBanner?: () => void;
}

interface StatusWarningBannerConfigsProps {
  hasRestrictionWarnings: boolean;
  hasCICOWarnings: boolean;
  canDeselectSamples: boolean;
  onDismissCICOWarning: () => void;
  onDismissRestrictionWarning?: () => void;
}

function getWarningTypeText(warningType: SampleStatusWarningType): string {
  switch (warningType) {
    case SampleStatusWarningType.CICO_WARNING:
      return 'reserved or checked out';
    case SampleStatusWarningType.SAMPLE_CONTROLS_WARNING:
      return hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)
        ? 'restricted'
        : 'not authorized for your use';
    default:
      assert(false, `Unexpected warning type: ${warningType}`);
  }
}

function dismissButtonText(isBlocking: boolean, warningType: SampleStatusWarningType): string {
  if (isBlocking) {
    return `De-select ${getWarningTypeText(warningType)} items`;
  }
  return 'Override warning';
}

function getStatusWarningBannerConfig(props: SingleStatusWarningBannerConfigProps): InlineBannerProps {
  if (props.canDismissBanner) {
    assert(props.onDismissBanner != null, 'Expected onDismissBanner function to be provided');
  }
  const action = props.canDismissBanner
    ? {
        actionType: InlineBannerActionType.CUSTOM as const,
        actionText: dismissButtonText(props.isBlocking, props.warningType),
        actionOnClick: props.onDismissBanner!,
      }
    : undefined;
  return {
    bannerType: props.isBlocking ? InlineBannerType.ERROR : InlineBannerType.WARNING,
    action,
    text: `Some items are ${getWarningTypeText(props.warningType)}.`,
  };
}

function getStatusWarningBannerConfigs(props: StatusWarningBannerConfigsProps): Array<InlineBannerProps> {
  const bannerConfigs: Array<InlineBannerProps> = [];
  if (props.hasRestrictionWarnings) {
    bannerConfigs.push(
      getStatusWarningBannerConfig({
        isBlocking: blockOnRestrictionWarnings(),
        warningType: SampleStatusWarningType.SAMPLE_CONTROLS_WARNING,
        canDismissBanner: blockOnRestrictionWarnings() ? props.canDeselectSamples : true,
        onDismissBanner: props.onDismissRestrictionWarning,
      })
    );
  }
  if (props.hasCICOWarnings) {
    bannerConfigs.push(
      getStatusWarningBannerConfig({
        isBlocking: false,
        warningType: SampleStatusWarningType.CICO_WARNING,
        canDismissBanner: true,
        onDismissBanner: props.onDismissCICOWarning,
      })
    );
  }
  return bannerConfigs;
}

function blockOnRestrictionWarnings(): boolean {
  return hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V3);
}

export default function SampleStatusWarningBannerStack(
  props: SampleStatusWarningBannerStackProps
): JSX.Element | null {
  const [hasRestrictionWarnings, setHasRestrictionWarnings] = useState(false);
  const [hasCICOWarnings, setHasCICOWarnings] = useState(false);
  const [initializedWarnings, setInitializedWarnings] = useState(false);
  const [hasOverriddenCICOWarnings, setHasOverriddenCICOWarnings] = useState(false);
  const [hasOverriddenRestrictionWarnings, setHasOverriddenRestrictionWarnings] = useState(false);
  const [dismissedRestrictionViaBanner, setDismissedRestrictionViaBanner] = useState(false);

  const includesUnavailable = (selectedSamples: Array<SampleStatusItem>): boolean =>
    selectedSamples.some((sample) => !isItemAvailableToCurrentUser(sample));
  const includesNoAccess = (selectedSamples: Array<SampleStatusItem>): boolean =>
    selectedSamples.some((sample) => !doesCurrentUserHaveAccessForItem(sample));

  const getHasOverridableWarnings = (hasUnavailable: boolean, hasNoAccess: boolean): boolean =>
    blockOnRestrictionWarnings() ? hasUnavailable : hasUnavailable || hasNoAccess;

  const onOverrideCICOWarnings = (): void => {
    setHasOverriddenCICOWarnings(true);
    setHasCICOWarnings(false);
  };
  const onDismissCICOWarningsWithTracking = (): void => {
    onOverrideCICOWarnings();
    trackStatusWarningDismissed(SampleStatusWarningType.CICO_WARNING, true, {
      ...props.trackingParamsWithSampleIds,
      sampleIds: props.selectedSampleIds,
    });
  };
  const dismissRestrictionWarning = (): void => {
    if (blockOnRestrictionWarnings()) {
      const selectedAccessibleSamples = props.samples.filter(
        (sample) => props.selectedSampleIds.includes(sample.id) && doesCurrentUserHaveAccessForItem(sample)
      );
      props.setSelectedSampleIds(selectedAccessibleSamples.map(({id}) => id));
    } else {
      setHasOverriddenRestrictionWarnings(true);
      setHasRestrictionWarnings(false);
    }
  };
  const onDismissRestrictionWarningWithTracking = (): void => {
    dismissRestrictionWarning();
    setDismissedRestrictionViaBanner(true);
    // Dismissing blocking restriction warning is tracked via the useEffect's call to trackStatusWarningChanges
    // to also capture when the warning disappears as a result of manually de-selecting samples
    if (!blockOnRestrictionWarnings()) {
      trackStatusWarningDismissed(SampleStatusWarningType.SAMPLE_CONTROLS_WARNING, true, {
        ...props.trackingParamsWithSampleIds,
        sampleIds: props.selectedSampleIds,
      });
    }
  };

  useOnMount(() => {
    const selectedSamples = props.samples.filter(({id}) => props.selectedSampleIds.includes(id));
    const hasUnavailable = includesUnavailable(selectedSamples);
    const hasNoAccess = includesNoAccess(selectedSamples);
    // If shouldProceed is true, that means we've already overridden the warnings. Any re-selected blocking
    // warnings will be caught by the useEffect hook. Consider all items shown, not just still selected
    // items to avoid re-surfacing non-blocking warnings if they're re-selected.
    if (props.shouldProceed) {
      if (includesUnavailable(props.samples)) {
        onOverrideCICOWarnings();
      }
      if (includesNoAccess(props.samples)) {
        dismissRestrictionWarning();
      }
    } else {
      setHasRestrictionWarnings(hasNoAccess);
      setHasCICOWarnings(hasUnavailable);
      trackStatusWarningChanges({
        trackingParamsWithSampleIds: props.trackingParamsWithSampleIds,
        shouldCheckCICOWarnings: true,
        shouldCheckRestrictionWarnings: true,
        baseMetadata: {
          hasCicoWarnings: hasUnavailable,
          hasRestrictionWarnings: hasNoAccess,
          canDeselectSamples: props.canDeselectSamples,
        },
        trackIfRestrictionDismissed: false,
      });
    }
    setInitializedWarnings(true);
  });

  useEffect(() => {
    // If the warnings have already been initialized (set in useOnMount), we want to check for warnings when
    // the selected samples update. We always check for blocking warnings under V3, and for nonblocking warnings
    // we only update if those warnings have not yet been overridden. This catches the cases where you can
    // select items and need to update an already rendered warning accordingly.
    if (initializedWarnings) {
      const selectedSamples = props.samples.filter(({id}) => props.selectedSampleIds.includes(id));

      const checkForRestrictionWarnings = blockOnRestrictionWarnings() || !hasOverriddenRestrictionWarnings;
      const hasNoAccess = checkForRestrictionWarnings
        ? includesNoAccess(selectedSamples)
        : hasRestrictionWarnings;
      const isUpdatingRestrictionWarnings =
        checkForRestrictionWarnings && hasNoAccess !== hasRestrictionWarnings;

      const checkForCICOWarnings = !hasOverriddenCICOWarnings;
      const hasUnavailable = checkForCICOWarnings ? includesUnavailable(selectedSamples) : hasCICOWarnings;
      const isUpdatingCICOWarnings = checkForCICOWarnings && hasUnavailable !== hasCICOWarnings;

      if (isUpdatingRestrictionWarnings) {
        setHasRestrictionWarnings(hasNoAccess);
      }
      if (isUpdatingCICOWarnings) {
        setHasCICOWarnings(hasUnavailable);
      }

      trackStatusWarningChanges({
        trackingParamsWithSampleIds: props.trackingParamsWithSampleIds,
        shouldCheckCICOWarnings: isUpdatingCICOWarnings,
        shouldCheckRestrictionWarnings: isUpdatingRestrictionWarnings,
        baseMetadata: {
          hasCicoWarnings: hasUnavailable,
          hasRestrictionWarnings: hasNoAccess,
          canDeselectSamples: props.canDeselectSamples,
        },
        trackIfRestrictionDismissed: true,
        dismissMetadata: {
          dismissedRestrictionViaBanner,
          selectedSampleIds: props.selectedSampleIds,
        },
      });
      setDismissedRestrictionViaBanner(false);
    }
  }, [
    props.selectedSampleIds,
    props.samples,
    initializedWarnings,
    hasRestrictionWarnings,
    hasCICOWarnings,
    hasOverriddenCICOWarnings,
    hasOverriddenRestrictionWarnings,
    props.trackingParamsWithSampleIds,
    props.canDeselectSamples,
    dismissedRestrictionViaBanner,
  ]);

  const {onChangeShouldProceed, shouldProceed, onChangeHasOverridableWarnings, hasOverridableWarnings} =
    props;
  useEffect(() => {
    const possibleNewShouldProceed = !hasCICOWarnings && !hasRestrictionWarnings;
    if (initializedWarnings && possibleNewShouldProceed !== shouldProceed) {
      onChangeShouldProceed(possibleNewShouldProceed);
    }
  }, [hasCICOWarnings, hasRestrictionWarnings, shouldProceed, initializedWarnings, onChangeShouldProceed]);

  useEffect(() => {
    const possibleNewHasOverridable = getHasOverridableWarnings(hasCICOWarnings, hasRestrictionWarnings);
    const hasOverridableChanged = initializedWarnings && hasOverridableWarnings !== possibleNewHasOverridable;
    if (hasOverridableChanged && onChangeHasOverridableWarnings != null) {
      onChangeHasOverridableWarnings(possibleNewHasOverridable);
    }
  }, [
    hasCICOWarnings,
    hasRestrictionWarnings,
    hasOverridableWarnings,
    initializedWarnings,
    onChangeHasOverridableWarnings,
  ]);

  const bannerConfigs = getStatusWarningBannerConfigs({
    hasRestrictionWarnings,
    hasCICOWarnings,
    canDeselectSamples: props.canDeselectSamples,
    onDismissCICOWarning: onDismissCICOWarningsWithTracking,
    onDismissRestrictionWarning: onDismissRestrictionWarningWithTracking,
  });
  if (bannerConfigs.length > 0) {
    return (
      <div className="u-small-spacing-bottom">
        <InlineBannerStack messages={bannerConfigs} />
      </div>
    );
  }
  return null;
}
